// Pure gating logic (no React) — unit-tested in gates.test.ts.

import type { LucideIcon } from 'lucide-react';
import { flagOn } from '@/lib/config';
import type { ErpMode } from '@/lib/env';
import { roleAtLeast, type Role } from '@/lib/me';
import type { TermKey } from '@/lib/terminology';
import { RAIL_ORDER, type ErpModule, type Gate, type MenuItem, type ModuleKey, type RailGroup } from './module-types';

export interface GateContext {
  flags: Record<string, boolean>;
  role: Role | null;
  capabilities: string[];
  mode: ErpMode;
  /** The active business's sector (GET /v1/erp/settings), for `Gate.sectors`. */
  sector?: string | null;
}

export type DenialReason = 'flag' | 'role' | 'capability' | 'mode';

/** Why a gate refuses, or null when it passes. Order: flag → mode → role → capability → anyOf. */
export function denial(g: Gate | null | undefined, ctx: GateContext): { reason: DenialReason; minRole?: Role } | null {
  if (!g) return null;
  if (g.flag && !flagOn(ctx.flags, g.flag)) return { reason: 'flag' };
  if (g.modes && g.modes.length > 0 && !g.modes.includes(ctx.mode)) return { reason: 'mode' };
  if (g.sectors && g.sectors.length > 0 && !(ctx.sector && g.sectors.includes(ctx.sector.toLowerCase()))) return { reason: 'flag' };
  if (g.minRole && !roleAtLeast(ctx.role, g.minRole)) return { reason: 'role', minRole: g.minRole };
  if (g.capability && ctx.role !== 'owner' && !ctx.capabilities.includes(g.capability)) return { reason: 'capability' };
  if (g.anyOf && g.anyOf.length > 0) {
    let first: { reason: DenialReason; minRole?: Role } | null = null;
    for (const alt of g.anyOf) {
      const d = denial(alt, ctx);
      if (!d) return null;
      first ??= d;
    }
    return first;
  }
  return null;
}

export function passes(g: Gate | null | undefined, ctx: GateContext): boolean {
  return denial(g, ctx) == null;
}

export function moduleGate(m: Pick<ErpModule, 'flag' | 'minRole' | 'anyOf'>): Gate {
  return m.anyOf && m.anyOf.length > 0 ? { flag: m.flag, minRole: m.minRole, anyOf: m.anyOf } : { flag: m.flag, minRole: m.minRole };
}

export function moduleVisible(m: ErpModule, ctx: GateContext): boolean {
  return passes(moduleGate(m), ctx);
}

export function visibleMenu(m: ErpModule, ctx: GateContext): MenuItem[] {
  return m.menu.filter((i) => passes(i, ctx));
}

export interface RailEntry {
  group: RailGroup;
  label: string;
  termLabel?: TermKey;
  icon: LucideIcon;
  /** Where clicking the rail entry goes (first visible menu row). */
  to: string;
  menu: MenuItem[];
  moduleKeys: ModuleKey[];
}

/**
 * The rail as this person sees it: groups with ≥1 visible module AND ≥1
 * visible menu row, in RAIL_ORDER. A group whose rows are all gated away is
 * absent (rule 6 — never an empty/locked entry). A visible module that
 * `replacesRail` a group takes its place: that group is absent while the
 * module is here (HANDOFF §17: «الحجوزات» for «المواعيد» under erp.hotel).
 */
export function buildRail(modules: ErpModule[], ctx: GateContext): RailEntry[] {
  const visible = modules.filter((m) => moduleVisible(m, ctx));
  // only a module that puts rows in the rail takes a group's place (nothing may vanish with nothing in its stead)
  const replaced = new Set(visible.filter((m) => m.replacesRail && visibleMenu(m, ctx).length > 0).map((m) => m.replacesRail as RailGroup));
  const out: RailEntry[] = [];
  for (const group of RAIL_ORDER) {
    if (replaced.has(group)) continue;
    const mods = visible.filter((m) => m.rail === group);
    if (mods.length === 0) continue;
    const menu = mods.flatMap((m) => visibleMenu(m, ctx));
    if (menu.length === 0) continue;
    const head = mods[0];
    out.push({ group, label: head.label, termLabel: head.termLabel, icon: head.icon, to: menu[0].to, menu, moduleKeys: mods.map((m) => m.key) });
  }
  return out;
}

/**
 * Which rail group owns a pathname: the most specific menu-row claim wins;
 * otherwise the group of the module whose route matched (`fallback`, from the
 * route handle's moduleKey), so a module page that is not a menu row (e.g.
 * /accounts/invoices, /reports/pnl) keeps its rail entry and menu.
 */
export function activeGroupFor(pathname: string, rail: RailEntry[], fallback: RailGroup | null = null, search = ''): RailGroup | null {
  let best: { group: RailGroup; len: number } | null = null;
  for (const e of rail) {
    for (const item of e.menu) {
      const len = claim(item, pathname, search);
      if (len >= 0 && (!best || len > best.len)) best = { group: e.group, len };
    }
  }
  if (best) return best.group;
  return fallback && rail.some((e) => e.group === fallback) ? fallback : null;
}

/** '/customers/:id/medical/*' against a pathname (':x' = one segment, trailing '*' = the rest). */
export function pathMatches(pattern: string, pathname: string): boolean {
  const p = pattern.split('/').filter(Boolean);
  const s = pathname.split('/').filter(Boolean);
  for (let i = 0; i < p.length; i++) {
    if (p[i] === '*' && i === p.length - 1) return true;
    if (i >= s.length) return false;
    if (!p[i].startsWith(':') && p[i] !== s[i]) return false;
  }
  return p.length === s.length;
}

/**
 * The rendered label of a rail entry, a menu row or a page: the terminology
 * word when the business's profile has it, else the static Arabic label — a
 * hotel-only word («housekeeping») asked by a tenant of another sector with
 * `erp.hotel` on must never show its key.
 */
export function labelOf(x: { label: string; termLabel?: TermKey }, t: (key: TermKey) => string): string {
  if (!x.termLabel) return x.label;
  const word = t(x.termLabel);
  return word === x.termLabel ? x.label : word;
}

/**
 * How specifically a row claims the location (-1 = not at all). An explicit
 * `match` beats a `to` prefix. A `to` that names a query («?view=departures»)
 * claims only when the location carries those parameters — the three
 * reservation views are three rows of which one is current; the EXACT bare
 * path (no search at all) is claimed by every view row alike, so the first of
 * them is the default view (activeMenuKey keeps the first among equals) — a
 * sub-path («/new», «/:id») or an unrelated query belongs to no view row.
 */
function claim(item: MenuItem, pathname: string, search = ''): number {
  let best = -1;
  const [p, q] = item.to.split('?');
  if (pathname === p || pathname.startsWith(`${p}/`)) {
    if (!q) best = p.length;
    else if (!search) best = pathname === p ? p.length : -1;
    else best = queryMatches(q, search) ? p.length + q.length + 1 : -1;
  }
  for (const m of item.match ?? []) if (pathMatches(m, pathname)) best = Math.max(best, 10_000 + m.length);
  return best;
}

/** Every parameter the row's query names is in the location's search with that value. */
function queryMatches(rowQuery: string, search: string): boolean {
  const want = new URLSearchParams(rowQuery);
  const have = new URLSearchParams(search);
  for (const [k, v] of want) if (have.get(k) !== v) return false;
  return true;
}

/** The ONE current row among a menu: the longest claim wins (so «السجل» /customers and «الاستيراد» /customers/import are never both active). */
export function activeMenuKey(items: readonly MenuItem[], pathname: string, search = ''): string | null {
  let best: { key: string; len: number } | null = null;
  for (const item of items) {
    const len = claim(item, pathname, search);
    if (len >= 0 && (!best || len > best.len)) best = { key: item.key, len };
  }
  return best?.key ?? null;
}

/** Is a menu row the current page? With its siblings, only the most specific row is. */
export function menuItemActive(item: MenuItem, pathname: string, siblings?: readonly MenuItem[], search = ''): boolean {
  if (siblings) return activeMenuKey(siblings, pathname, search) === item.key;
  return claim(item, pathname, search) >= 0;
}

/** Phone bottom bar: first five rail entries; the rest go behind «المزيد». */
export function splitForBottomBar(rail: RailEntry[], max = 5): { primary: RailEntry[]; more: RailEntry[] } {
  if (rail.length <= max) return { primary: rail, more: [] };
  return { primary: rail.slice(0, max), more: rail.slice(max) };
}
